import { McpAuthorizePage } from './pages/McpAuthorize.js';
// --- App shell (ocn-S184-P0) -------------------------------------
// Thin shell after the App.tsx decomposition (arch-review Finding 5):
// App owns auth gating, the OnboardingProvider, the topbar/sidebar chrome,
// global project/run fetches, and page state. In-shell page rendering +
// the S182 user_tier route gate live in app/AppRouter.tsx; the page switch
// itself lives in app/pageRegistry.tsx; the heavy page bodies
// (ProjectDetailPage, AddProjectPage, RecentJournal, ManusIngestSection) are
// now their own modules under pages/.
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Menu, LogOut } from "lucide-react";
import api from "./api";
import type { HealthResponse } from "./api";
import { useApi } from "./useApi";
import { useAuth } from "./context/AuthContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { BrandedDialogProvider, useBrandedDialog } from "./components/BrandedDialog";
import { OfflineBanner } from "./components/layout/OfflineBanner";
import { SidebarWithTier } from "./components/layout/Sidebar";

// Full-screen gate pages (rendered without the shell chrome)
import { Login } from "./pages/Login";
import { AuthCallback } from "./pages/AuthCallback";
import { PasswordResetConfirm } from "./pages/PasswordResetConfirm";
import { PlanSelector } from "./pages/PlanSelector";
import OnboardingChat from "./pages/OnboardingChat";
import Welcome from "./pages/Welcome";
import WelcomeConnect from "./pages/WelcomeConnect";
import { planBadgeLabel, planDisplayName, statusDisplayName } from "./pages/Account";

// Encapsulated page router + the ctx contract it consumes
import { AppRouter } from "./app/AppRouter";
import type { RouterCtx } from "./app/pageRegistry";
import { resolveLanding } from "./app/tier-visibility";
import { formatCreditsAsUsd } from "./lib/credits";
import { resolveCodexConsentReturn } from "./lib/codex-consent-return";

// Header badges are buttons; keep their existing badge look.
const badgeButtonReset: React.CSSProperties = { cursor: 'pointer', font: 'inherit' };

/* =============================================================== */
/*  APP SHELL                                                       */
/* =============================================================== */

function AppContent() {
  const [page, setPage] = useState(() => {
    if (window.location.pathname === '/auth/callback') return 'auth-callback';
    const hierarchyMatch = window.location.pathname.match(/^\/projects\/([^/]+)\/chats\/([^/]+)$/);
    if (hierarchyMatch) return `hierarchy-chat:${hierarchyMatch[1]}:${hierarchyMatch[2]}`;
    const params = new URLSearchParams(window.location.search);
    return params.get('page') || 'overview';
  });

  // Browser Back/Forward (canary 2026-10-02: Back left the app because in-app
  // navigation never touched history). Mirror every page change into ?page=
  // and restore it on popstate. The first change after a cold load (e.g. the
  // landing redirect) REPLACES the entry so Back never bounces into a
  // redirect loop.
  const historyPrimed = useRef(false);
  // Stale-bundle guard (canary 2026-10-03: a long-lived tab kept showing copy removed
  // the day before). Remember the deployed commit at load; when /version.json reports a
  // newer one, the next in-app navigation does a full page load instead of pushState.
  const loadedCommit = useRef<string | null>(null);
  const bundleStale = useRef(false);
  useEffect(() => {
    const check = async (): Promise<void> => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { commit } = await res.json() as { commit?: string };
        if (!commit || commit === 'dev') return;
        if (loadedCommit.current === null) loadedCommit.current = commit;
        else if (commit !== loadedCommit.current) bundleStale.current = true;
      } catch { /* offline or blocked: keep the current bundle */ }
    };
    void check();
    const timer = setInterval(() => { void check(); }, 5 * 60_000);
    const onFocus = (): void => { void check(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, []);
  useEffect(() => {
    if (page === 'auth-callback' || page === 'password-reset-confirm' || page.startsWith('hierarchy-chat:')) return;
    const current = new URLSearchParams(window.location.search);
    if ((current.get('page') || 'overview') === page && window.location.pathname === '/') { if (current.has('page')) historyPrimed.current = true; return; }
    current.set('page', page);
    const url = `/?${current.toString()}${window.location.hash}`;
    if (historyPrimed.current && bundleStale.current) { window.location.assign(url); return; }
    if (historyPrimed.current) window.history.pushState({ page }, '', url);
    else window.history.replaceState({ page }, '', url);
    historyPrimed.current = true;
  }, [page]);
  useEffect(() => {
    const onPop = (): void => {
      const hierarchy = window.location.pathname.match(/^\/projects\/([^/]+)\/chats\/([^/]+)$/);
      if (hierarchy) { setPage(`hierarchy-chat:${hierarchy[1]}:${hierarchy[2]}`); return; }
      setPage(new URLSearchParams(window.location.search).get('page') || 'overview');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const { user, subscriber, subscriberResolved, subscriberFetchFailed, loading: authLoading, sessionExpired, signOut, signInWithGitHub, userTier, refreshSubscriber } = useAuth();
  const { confirm: confirmDialog, alert: alertDialog } = useBrandedDialog();
  const [showReauthentication, setShowReauthentication] = useState(false);
  // ocn-S545-P0: fresh-signup chat onboarding gate. AuthCallback.tsx (ocn-S418-P0)
  // redirects a fresh magic-link verify to ?page=subscriber-chat&onboarding=1 —
  // captured once at mount so it stays true through the whole onboarding flow
  // even after onFinish cosmetically strips the query param from the URL bar.
  const onboardingParam = useMemo(
    () => new URLSearchParams(window.location.search).get('onboarding') === '1',
    [],
  );
  // ocn: whether the user arrived with an explicit ?page= deep-link. Chatbox-first
  // landing (below) only redirects when there is NO explicit page, so deep-links win.
  const explicitPageParam = useMemo(
    () => new URLSearchParams(window.location.search).get('page'),
    [],
  );

  const projectJustCreated = React.useRef(false);
  const { data: health, error: healthError, refetch: refetchHealth } = useApi(() => api.health(), []);
  useEffect(() => {
    const retry = () => refetchHealth();
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') retry(); };
    const interval = window.setInterval(retry, 45_000);
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refetchHealth]);
  const { data: usage } = useApi(
    () => subscriber ? api.getUsage() : Promise.resolve(null),
    [subscriber],
  );
  const { data: projects, loading: projLoading, error: projError, refetch: refetchProjects } = useApi(
    () => (subscriber ? api.listProjects() : Promise.resolve([])),
    [subscriber?.id],
  );
  // The MCP Connect repo picker announces a newly connected repository.
  useEffect(() => {
    const onChanged = () => refetchProjects();
    window.addEventListener('ocn:projects-changed', onChanged);
    return () => window.removeEventListener('ocn:projects-changed', onChanged);
  }, [refetchProjects]);

  const safeProjects = useMemo(() => projects || [], [projects]);

  const { data: allRuns } = useApi(() => api.logRuns(undefined, 50), []);
  const safeRuns = useMemo(() => (Array.isArray(allRuns) ? allRuns : []), [allRuns]);

  useEffect(() => {
    if (user && subscriber && typeof window !== 'undefined' && (window as any).LumenIQSupport) {
      (window as any).LumenIQSupport.init({
        appId: 'ocnexus',
        userId: user.id ?? null,
        userEmail: user.email ?? null,
        userPlan: subscriber.plan ?? null,
        position: 'bottom-right',
      });
    }
  }, [user, subscriber]);

  const projectsLoadFailed = !projLoading && (projects === null || !!projError);
  useEffect(() => {
    if (
      user && subscriber && !authLoading && !projLoading &&
      !projectsLoadFailed &&
      Array.isArray(projects) && projects.length === 0 &&
      !projectJustCreated.current &&
      !['welcome', 'welcome-connect', 'wizard', 'addproject', 'new-project', 'auth-callback', 'overview', 'subscriber-chat', 'settings', 'modules', 'credentials', 'runs', 'logs', 'sync', 'journal', 'guardian', 'skills', 'infra', 'costs', 'safety', 'repo-list', 'profile', 'subscription', 'account', 'mcp-connect', 'metrics', 'config'].includes(page) &&
      !page.startsWith('project:') &&
      !page.startsWith('subscriber-chat:')
    ) {
      // ocn-S818-P0 Part 2: the empty-state CTA is the SAME canonical
      // three-card entry screen every other entry affordance uses -- the
      // legacy Welcome two-path-fork ('welcome' -> connect/create) is no
      // longer the auto-triggered destination. The 'welcome'/'welcome-connect'
      // routes themselves are left intact (unlinked, not deleted).
      setPage('new-project');
    }
  }, [user, subscriber, authLoading, projLoading, projectsLoadFailed, projects, page]);

  const isProjectPage = page.startsWith("project:");
  const projectId = isProjectPage ? page.split(":")[1] : null;

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useEffect(() => { setMobileSidebarOpen(false); }, [page]);

  // ocn-S321-P0: collapsible left rail. Defaults expanded for every tier.
  // React state only (no localStorage) per the spec's artifact rules.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);

  // ocn-S321-P0: detect when we should maximize chat (builder + subscriber-chat page)
  const isSubscriberChatPage = page === 'subscriber-chat' || page.startsWith('subscriber-chat:');
  const chatMaximize = userTier === 'builder' && isSubscriberChatPage;

  const chatPrefillRef = React.useRef<string | null>(null);
  const chatSessionRef = React.useRef<string | null>(null);
  const lastActiveProjectRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (isProjectPage && projectId) lastActiveProjectRef.current = projectId;
  }, [isProjectPage, projectId]);

  // ocn-S567-P0: only treat a missing subscriber as "orphaned" AFTER the
  // subscriber fetch has actually settled (subscriberResolved). Previously
  // this fired during the transient render right after sign-in — when `user`
  // is set but `fetchSubscriber` has not yet resolved — misreading a
  // still-loading subscriber as orphaned and calling signOut(), which bounces
  // the just-authenticated user to the marketing site. Gating on
  // subscriberResolved keeps the genuine-orphan safety net (no subscribers row)
  // while eliminating the login-race false positive.
  // ocn: only sign out on a CONFIRMED-absent subscriber row. A failed fetch
  // (subscriberFetchFailed) — transient/RLS/network — must never orphan a
  // validly-authenticated user and bounce them to the marketing site.
  const orphanedSession = !authLoading && !!user && !subscriber && subscriberResolved && !subscriberFetchFailed;
  useEffect(() => {
    if (orphanedSession) {
      console.warn('[Auth] User authenticated but no subscriber record - signing out');
      signOut();
    }
  }, [orphanedSession, signOut]);

  // ocn: chatbox-first landing. A builder-tier subscriber who opens the app with
  // no explicit ?page= deep-link lands in the factory chat (subscriber-chat)
  // instead of the overview dashboard. Fires once after auth settles so the user
  // can still navigate away (e.g. to overview) afterward.
  const landingRedirectRef = React.useRef(false);
  useEffect(() => {
    if (landingRedirectRef.current) return;
    if (authLoading || !user || !subscriber || !subscriberResolved) return;
    landingRedirectRef.current = true;
    if (userTier === 'builder' && !explicitPageParam && page === 'overview') {
      const landing = resolveLanding(projectsLoadFailed ? null : projects, subscriber);
      // ocn-S818-P0 Part 1: a forge-entitled cold start lands on the SAME
      // canonical three-card entry screen every other entry point uses --
      // 'welcome-connect' is no longer a direct landing target, only a
      // capability reachable from a card/menu.
      setPage(
        landing.kind === 'project-chat' ? `subscriber-chat:project:${landing.projectId}` :
          landing.kind === 'forge-cold-start' ? 'new-project' : 'subscriber-chat:app_wizard',
      );
    }
  }, [authLoading, user, subscriber, subscriberResolved, userTier, explicitPageParam, page]);

  // Return from Mission Control chatbox consent (?page=subscriber-chat&codex_consent=1): once the
  // subscriber is known, strip the marker param and, when this tab saved the chat it left (same user,
  // unexpired, single use), go back to it. No marker: stay on the plain chat page. Never starts a run.
  const consentReturnRef = React.useRef(false);
  useEffect(() => {
    if (consentReturnRef.current || authLoading || !subscriber?.id) return;
    consentReturnRef.current = true;
    const consentReturn = resolveCodexConsentReturn(window.location.search, subscriber.id);
    if (!consentReturn) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${consentReturn.search}${window.location.hash}`);
    if (consentReturn.route) setPage(consentReturn.route);
  }, [authLoading, subscriber?.id]);

  // --- Auth Gates ---------------------------------------------
  if (authLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, background: 'var(--bg)', color: 'var(--text-dim)' }}>
        <div className="spinner" style={{ width: 24, height: 24 }} />
        <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>Checking session...</span>
      </div>
    );
  }

  if (page === 'auth-callback') return <AuthCallback onAuthenticated={() => setPage('overview')} />;
  if (page === 'password-reset-confirm') return <PasswordResetConfirm onDone={() => setPage('login')} />;
  if (sessionExpired && showReauthentication) {
    return <Login onAuthenticated={() => setPage('overview')} />;
  }

  if (sessionExpired) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16, background: 'var(--bg)', color: 'var(--text)' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>Session expired</div>
        <p style={{ color: 'var(--text-dim)', margin: 0, fontSize: 'var(--text-sm)', textAlign: 'center', maxWidth: 360 }}>
          Your session could not be refreshed. Please sign in again to continue.
        </p>
        <button
          onClick={() => signInWithGitHub()}
          style={{
            marginTop: 8,
            padding: '10px 24px',
            background: 'var(--accent, #00E5FF)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Sign in with GitHub
        </button>
        <button
          onClick={() => setShowReauthentication(true)}
          style={{
            padding: '10px 24px',
            background: 'transparent',
            color: 'var(--accent, #00E5FF)',
            border: '1px solid var(--accent, #00E5FF)',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Sign in with email or password
        </button>
      </div>
    );
  }

  if (!user) return <Login onAuthenticated={() => setPage('overview')} />;

  if (orphanedSession) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, background: 'var(--bg)', color: 'var(--text-dim)' }}>
        <div className="spinner" style={{ width: 24, height: 24 }} />
        <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>Resetting session...</span>
      </div>
    );
  }

  // ocn-S545-P0: the ratified Path-2 chat onboarding replaces the tier-ladder
  // PlanSelector ONLY for a genuinely fresh, still-unsubscribed ?onboarding=1
  // arrival — every other unsubscribed case (e.g. a churned subscriber) keeps
  // the existing PlanSelector behavior unchanged (zero regression risk).
  if (subscriber && !subscriber.stripe_customer_id && onboardingParam) {
    return (
      <OnboardingChat
        onFinish={() => {
          void refreshSubscriber();
          window.history.replaceState(null, '', window.location.pathname + window.location.hash);
        }}
      />
    );
  }

  // ocn: soft-gate the paywall for builder tier. Unpaid builder subscribers are
  // let into the shell (they land in the factory chat and can run the wizard);
  // the build action routes them to plan selection and the server enforces the
  // paywall (402 plan_required). Developer tier keeps the hard wall unchanged.
  if (subscriber && !subscriber.stripe_customer_id && userTier !== 'builder') {
    return <PlanSelector user={user} subscriber={subscriber} signOut={signOut} />;
  }

  if (page === 'welcome') {
    return (
      <OnboardingProvider projects={safeProjects} runs={safeRuns}>
        <Welcome
          onConnect={() => setPage('welcome-connect')}
          onCreateNew={() => setPage('wizard')}
          onSkip={() => { projectJustCreated.current = true; setPage('overview'); setTimeout(() => { projectJustCreated.current = false; }, 5000); }}
        />
      </OnboardingProvider>
    );
  }

  if (page === 'welcome-connect') {
    return (
      <OnboardingProvider projects={safeProjects} runs={safeRuns}>
        <WelcomeConnect
          onBack={() => setPage('welcome')}
          onComplete={(pid: string) => {
            projectJustCreated.current = true;
            void api.createSubscriberChat(pid)
              .then(result=>setPage(`subscriber-chat:chat:${result.chatSessionId}`))
              .catch(()=>setPage(`subscriber-chat:project:${pid}`));
            setTimeout(() => { projectJustCreated.current = false; }, 5000);
          }}
          registeredProjects={safeProjects}
        />
      </OnboardingProvider>
    );
  }

  // ocn: on-demand plan selection (the in-chat soft-gate navigates here when an
  // unpaid subscriber tries to build). Full-screen gate like welcome.
  if (page === 'plan' && subscriber) {
    return <PlanSelector user={user} subscriber={subscriber} signOut={signOut} />;
  }

  const routerCtx: RouterCtx = {
    go: setPage,
    projects: safeProjects,
    projLoading,
    refetchProjects,
    chatPrefillRef,
    chatSessionRef,
    lastActiveProjectRef,
  };

  const displayName = (subscriber as unknown as { full_name?: string | null } | null)?.full_name?.trim()
    || user.email?.split('@')[0] || 'user';
  const usageBalance = formatCreditsAsUsd(usage?.creditsRemaining ?? 0);

  return (
    <OnboardingProvider projects={safeProjects} runs={safeRuns}>
    <OfflineBanner />
    <div className={`app-shell${sidebarCollapsed ? ' app-shell--sidebar-collapsed' : ''}`}>
      <style>{`
        @keyframes progress{0%{transform:translateX(-100%);}50%{transform:translateX(0%);}100%{transform:translateX(100%);}}
      `}</style>

      <div className={`sidebar-overlay${mobileSidebarOpen ? ' visible' : ''}`} onClick={() => setMobileSidebarOpen(false)} />

      {/* TOPBAR */}
      <div className="topbar">
        <button className="mobile-menu-btn" onClick={() => setMobileSidebarOpen((o: boolean) => !o)} aria-label="Toggle menu"><Menu size={18} /></button>
        <div className="topbar-brand">
          <div className="topbar-brand-icon"><svg viewBox="0 0 48 28" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="16"><defs><linearGradient id="tb-grad" x1="0" y1="14" x2="42" y2="14" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#00E5FF"/><stop offset="100%" stopColor="#8B5CF6"/></linearGradient></defs><path d="M13 6C7.5 6 3 9.9 3 14s4.5 8 10 8c3.2 0 6-1.4 8-3.6C23 20.6 25.8 22 29 22c5.5 0 10-3.9 10-8s-4.5-8-10-8c-3.2 0-6 1.4-8 3.6C19 7.4 16.2 6 13 6z" stroke="url(#tb-grad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
          <span className="topbar-brand-name">OCNexus</span>
        </div>
        <div className="topbar-sep" />
        <div className="topbar-right">
          {usage && (
            <button
              type="button"
              onClick={() => setPage('subscription')}
              className={`credits-badge${usage.warningLevel !== 'none' ? ` credits-badge--${usage.warningLevel}` : ''}`}
              title={`${usageBalance} usage balance. Open Subscription`}
              style={badgeButtonReset}
            >
              {usageBalance} balance
            </button>
          )}
          {subscriber && (
            <button
              type="button"
              onClick={() => setPage('subscription')}
              className="plan-badge"
              title={`${planDisplayName(subscriber.plan)} (${statusDisplayName(subscriber.status)}). Open Subscription`}
              style={badgeButtonReset}
            >
              {planBadgeLabel(subscriber.plan)}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPage('profile')}
            className="org-badge"
            title="My profile"
            style={badgeButtonReset}
          >
            {displayName}
          </button>
          <button className="topbar-signout" onClick={signOut} title="Sign out"><LogOut size={14} /></button>
        </div>
      </div>

      {/* SIDEBAR (ocn-S321-P0: collapsible, default-expanded for all tiers) */}
      <SidebarWithTier
        page={page} go={setPage} projects={safeProjects} health={healthError ? null : health as HealthResponse | null}
        mobileOpen={mobileSidebarOpen}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c: boolean) => !c)}
        onRemoveProject={async (id: string) => {
          try {
            await api.removeProject(id);
            refetchProjects();
            if (page === `project:${id}`) setPage('overview');
          } catch (err) {
            const error = err as Error & { status?: number };
            const message = error.message || 'Could not remove project';
            if (error.status === 409) {
              const archive = await confirmDialog({
                title: 'Project cannot be deleted',
                message,
                confirmLabel: 'Archive instead',
                cancelLabel: 'Cancel',
              });
              if (!archive) return;
              try {
                await api.archiveProject(id);
                refetchProjects();
                if (page === `project:${id}`) setPage('overview');
              } catch (archiveErr) {
                const archiveMessage = archiveErr instanceof Error ? archiveErr.message : 'Could not archive project';
                await alertDialog({ title: 'Archive failed', message: archiveMessage });
              }
            } else {
              await alertDialog({ title: 'Could not remove project', message });
            }
          }
        }} />

      {/* MAIN — ocn-S321-P0: for builder + subscriber-chat, skip the constraining wrapper
           so the chat surface claims the full available width and height. */}
      {chatMaximize ? (
        <main className="page-main page-main--chat-maximize">
          <AppRouter page={page} ctx={routerCtx} />
        </main>
      ) : (
        <main className="page-main" style={{ overflowY: "auto" }}>
          <div style={{ maxWidth: 960, margin: "0 auto", padding: "var(--space-5) var(--space-6) var(--space-6)" }}>
            <AppRouter page={page} ctx={routerCtx} />
          </div>
        </main>
      )}
    </div>
    </OnboardingProvider>
  );
}

export default function App() {
  return <BrandedDialogProvider>{window.location.pathname === '/mcp/authorize' ? <McpAuthorizePage /> : <AppContent />}</BrandedDialogProvider>;
}
